import {EMPTY, from, Observable} from 'rxjs';
import {catchError, map, mergeMap} from 'rxjs/operators';
import {firstOrDefault} from "./ArrayHelpers";
import {NotificationService} from '../_services/notification.service';
import {PayloadProtectionService} from '../_services/payload-protection.service';

/**
 * Swaps an encoded `results` string back for the real payload. The server marks encoded responses
 * (student callers, and any endpoint it protects for everyone), so anything unmarked passes straight
 * through.
 */
async function decodeProtectedResults<TResult>(data: ApiResult<TResult>): Promise<ApiResult<TResult>> {
    if (data == null || !data.protected || typeof data.results !== 'string') {
        return data;
    }

    const protection = PayloadProtectionService.current;
    if (protection == null) {
        throw new Error('This response is protected but no payload key is available');
    }

    return {...data, results: await protection.decode(data.results) as TResult[] | TResult};
}

function notifyError(message: string): void {
    const notify = NotificationService.current;
    if (notify) {
        notify.error(message);
    } else {
        alert(message);
    }
}

export interface ApiResult<TResult> {
    status: number;
    statusText: string;
    /** Set by the server when `results` arrives encoded for this session. */
    protected?: boolean;
    results: TResult[] | TResult;
    totalApiLoadTime: number;
    totalProcessingTime: number;
    message: string;
    exception: string;
}

export class ApiHelper {

    preListenFactory: () => void;

    catchError<TResult>(observable: Observable<ApiResult<TResult>>): Observable<ApiResult<TResult>> {
        return observable
            .pipe(
                map(item => item),
                catchError(err => {
                    notifyError(`API Transmission Error: ${err}`);
                    return EMPTY;
                }),
            );
    }

    preListen(factory: () => void): ApiHelper {
        this.preListenFactory = factory;

        return this;
    }


    listen<TResult>(
        observable: Observable<ApiResult<TResult>>,
        preCheck?: (result: TResult | TResult[], messages: string[]) => boolean
    ): Observable<TResult[]> {

        if (this.preListenFactory != null && typeof this.preListenFactory === 'function') {
            this.preListenFactory();
        }

        return this.catchError(observable)
            .pipe(
                mergeMap(data => from(decodeProtectedResults(data))),
                //catchError above only covers the request itself - it is applied to the source, so
                //a decode failure would sail past it and surface raw at the call site. this sits
                //between the decode and the map so it catches decode only, not the map's own
                //throw for a failed api status.
                catchError(error => {
                    notifyError(`API Response Error: ${error?.message ?? error}`);
                    return EMPTY;
                }),
                map(data => {
                    switch (data.status) {
                        case 1:
                            if (preCheck != null) {
                                const messages: string[] = [];
                                if (!preCheck(data.results, messages)) {
                                    throw new Error(`Validation Failed - ${messages.join('\n')}`);
                                }
                            }

                            switch (true) {
                                case (data == null || data.results == null):
                                    return null;
                                case Array.isArray(data.results):
                                    return data.results as TResult[];
                                default:
                                    return [data.results] as TResult[];
                            }
                            break;

                        default:
                            throw new Error(data.message);
                            break;
                    }
                }));
    }

    listenOne<TResult>(observable: Observable<ApiResult<TResult>>, preCheck?: (TResult) => boolean): Observable<TResult> {
        return this.listen(observable, preCheck)
            .pipe(
                map(data => {
                    if (Array.isArray(data)) {
                        if (data.length > 1) {
                            notifyError('More than 1 response received');
                            throw new Error('More than 1 response received');
                        }
                        return firstOrDefault(data);
                    } else {
                        return data;
                    }
                })
            );
    }
}
